<template>
  <div class="reservation-review-section">
    <div class="review-wrapper">
      <!-- Title -->
      <h2 class="review-title">
        Parlez-nous de vos séjours
      </h2>
      <p class="review-subtitle">
        Vous avez récemment séjourné, comment vos séjours se sont-ils passés ?
      </p>

      <!-- Scrollable list of reservations -->
      <div class="review-reservations-list">
        <div v-for="(reservation, index) in reservations" :key="reservation.id" class="review-reservation-item">
          <!-- Lodging Card -->
          <div class="review-lodging-card">
            <!-- Image -->
            <div class="review-lodging-image">
              <img
                v-if="reservation.lodging && reservation.lodging.photo_banner"
                :src="reservation.lodging.photo_banner"
                :alt="reservation.lodging.name"
              />
              <img
                v-else
                src="https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=200&q=80"
                alt="Lodging"
              />
            </div>

            <!-- Lodging Info -->
            <div class="review-lodging-body">
              <div class="review-lodging-name-row">
                <span class="review-lodging-name merriweather-reg-15">
                  {{ reservation.lodging ? reservation.lodging.name : 'Hébergement' }}
                </span>
                <!-- Stars -->
                <span class="review-lodging-stars" v-if="reservation.lodging && reservation.lodging.standing && parseInt(reservation.lodging.standing) > 0">
                  <img src="../../images/Etoiles.svg" v-if="reservation.lodging.standing"
                    v-for="i in Math.max(0, parseInt(reservation.lodging.standing) || 0)" :key="i" />
                  <img v-if="reservation.lodging.standing && parseInt(reservation.lodging.standing) > 0" src="../../images/blanc.png"
                    alt="white" />
                </span>
              </div>

              <!-- Type badge -->
              <div class="review-lodging-type-badge raleway-semi-p11 text-uppercase" v-if="reservation.lodging && reservation.lodging.type">
                {{ getTypeLabel(reservation.lodging.type) }}
              </div>

              <!-- Address -->
              <p class="review-lodging-address raleway-reg-p15" v-if="reservation.lodging && reservation.lodging.full_address">
                {{ reservation.lodging.full_address }}
              </p>

              <!-- Dates -->
              <p class="review-lodging-dates raleway-reg-p15">
                Du {{ formatDate(reservation.checkin) }} au {{ formatDate(reservation.check_out) }}
              </p>
            </div>

            <!-- Toggle Button -->
            <button class="review-toggle-btn" @click="toggleForm(index)" type="button">
              <span>AJOUTER UN AVIS</span>
              <svg
                :class="['review-toggle-chevron', { 'rotated': openForms[index] }]"
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>

          <!-- Review Form (collapsible) - Below each card -->
          <div v-if="openForms[index]" class="review-form-container">
            <div class="review-form-divider"></div>

            <!-- Two-column layout: ratings left, comment right -->
            <div class="review-form-grid">
              <!-- Left: Rating sections -->
              <div class="review-ratings-col">
                <!-- Global Rating -->
                <div class="review-rating-group">
                  <p class="review-rating-label">NOTE GLOBALE</p>
                  <p class="review-rating-desc">Comment évalueriez-vous le niveau de confort et la qualité des prestations proposées ?</p>
                  <div class="review-stars-row">
                    <button
                      v-for="star in 5"
                      :key="star"
                      @click="formDataByIndex[index].global = star"
                      @mouseover="hoverGlobal = star"
                      @mouseleave="hoverGlobal = 0"
                      :class="['review-star-btn', { 'active': (hoverGlobal || formDataByIndex[index].global) >= star }]"
                      type="button"
                      :aria-label="`${star} étoiles`">
                      ☆
                    </button>
                  </div>
                </div>
              </div>

              <!-- Right: Comment textarea -->
              <div class="review-comment-col">
                <p class="review-rating-label">VOTRE COMMENTAIRE</p>
                <p class="review-rating-desc">Partagez votre expérience avec les futurs voyageurs...</p>
                <textarea
                  v-model="formDataByIndex[index].review"
                  class="review-textarea"
                  placeholder="Saisissez votre commentaire ici..."
                  rows="8"
                  maxlength="500"
                ></textarea>
                <p class="review-char-count">{{ formDataByIndex[index].review.length }}/500</p>
              </div>
            </div>

            <!-- Submit -->
            <div class="review-form-actions">
              <button
                @click="submitReview(index)"
                :disabled="!isFormValidByIndex(index) || submittingIndex === index"
                class="review-submit-btn"
                type="button">
                <span v-if="submittingIndex !== index">SOUMETTRE MON AVIS</span>
                <span v-else>Envoi en cours...</span>
              </button>
              <p v-if="submitErrorByIndex[index]" class="review-error-msg">{{ submitErrorByIndex[index] }}</p>
              <p v-if="submitSuccessByIndex[index]" class="review-success-msg">Merci pour votre avis !</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import fetcher from 'packs/shared/api.js';

export default {
  name: 'ReservationReview',
  props: {
    reservations: {
      type: Array,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      openForms: {},
      hoverGlobal: 0,
      formDataByIndex: {},
      submittingIndex: null,
      submitErrorByIndex: {},
      submitSuccessByIndex: {},
    };
  },
  computed: {
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '';
      const months = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
      const d = new Date(dateStr);
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
    },
    getTypeLabel(type) {
      const typeLabels = {
        'Hotel': 'Hôtels',
        'Maison et Villa': 'Maisons et Villas',
        'Guestroom': 'Chambres d\'Hôtes',
        'Lodge': 'Lodges et Lieux Insolites',
        'Appartment': 'Appartements',
        'Chalet': 'Chalets',
      };
      return typeLabels[type] || type;
    },
    toggleForm(index) {
      this.$set(this.openForms, index, !this.openForms[index]);
    },
    isFormValidByIndex(index) {
      const formData = this.formDataByIndex[index];
      if (!formData) return false;
      return formData.global > 0 && formData.review.trim().length > 0;
    },
    async submitReview(index) {
      if (!this.isFormValidByIndex(index) || this.submittingIndex !== null) return;

      const reservation = this.reservations[index];
      if (!reservation) return;

      this.submittingIndex = index;
      this.$set(this.submitErrorByIndex, index, '');
      this.$set(this.submitSuccessByIndex, index, false);

      try {
        await fetcher.post(`/reservations/${reservation.id}/ratings`, {
          rating: {
            global: this.formDataByIndex[index].global,
            review: this.formDataByIndex[index].review,
          },
        });

        this.$set(this.submitSuccessByIndex, index, true);
        this.$set(this.formDataByIndex, index, { global: 0, review: '' });
        this.$set(this.openForms, index, false);

        this.$emit('review-submitted');

        setTimeout(() => {
          this.$set(this.submitSuccessByIndex, index, false);
        }, 3000);
      } catch (error) {
        console.error('Error submitting review:', error);
        this.$set(this.submitErrorByIndex, index, error.response?.data?.error || 'Une erreur est survenue lors de l\'envoi de votre avis.');
      } finally {
        this.submittingIndex = null;
      }
    },
  },
  mounted() {
    // Initialize form data for each reservation
    this.reservations.forEach((reservation, index) => {
      this.$set(this.formDataByIndex, index, { global: 0, review: '' });
      this.$set(this.openForms, index, false);
      this.$set(this.submitErrorByIndex, index, '');
      this.$set(this.submitSuccessByIndex, index, false);
    });
  },
};
</script>

<style scoped>
/* ===== SECTION WRAPPER ===== */
.reservation-review-section {
  width: 100%;
  background: #f9f9f9;
  padding: 40px 20px;
}

.review-wrapper {
  max-width: 90%;
  margin: 0 auto;
}

/* ===== SCROLLABLE LIST ===== */
.review-reservations-list {
  max-height: 400px;
  overflow-y: auto;
  border-radius: 14px;
  margin-bottom: 20px;

  @media (max-width: 767px) {
    max-height: 100%;
  }
}

.review-reservations-list::-webkit-scrollbar {
  width: 6px;
}

.review-reservations-list::-webkit-scrollbar-track {
  background: #3B6152;
  border-radius: 10px;
}

.review-reservations-list::-webkit-scrollbar-thumb {
  background: #3B6152;
  border-radius: 10px;
}

.review-reservations-list::-webkit-scrollbar-thumb:hover {
  background: #3B6152;
}

/* ===== TITLE ===== */
.review-title {
  font-family: 'Merriweather', serif;
  font-size: 24px;
  font-weight: 400;
  color: #000;
  margin-bottom: 8px;
}

.review-subtitle {
  font-size: 15px;
  color: #000;
  margin-bottom: 24px;
  font-family: 'Raleway', sans-serif;
}

/* ===== RESERVATION ITEM WRAPPER ===== */
.review-reservation-item {
  margin-bottom: 12px;
}

.review-reservations-list .review-reservation-item:last-child {
  margin-bottom: 0;
}

/* ===== LODGING CARD (matches resa-card / wish-card style) ===== */
.review-lodging-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fff;
  border-radius: 14px 14px 0 0;
  padding: 16px 20px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
}

.review-lodging-image {
  flex-shrink: 0;
  width: 117px;
  height: 103px;
  border-radius: 10px;
  overflow: hidden;
  align-self: center;
}

.review-lodging-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.review-lodging-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.review-lodging-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.review-lodging-name {
  font-family: 'Merriweather', serif;
  font-size: 15px;
  font-weight: 400;
  color: #000;
}

.review-lodging-stars {
  display: flex;
  align-items: center;
  gap: 1px;
}

.review-lodging-stars img {
  width: 15px;
  height: 15px;
}

.review-lodging-type-badge {
  border: 1px solid #2C5E4B;
  border-radius: 15px;
  color: #2C5E4B;
  padding: 3px 12px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  font-size: 11px;
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.review-lodging-address {
  font-size: 15px;
  color: #000;
  margin: 0;
  font-family: 'Raleway', sans-serif;
}

.review-lodging-dates {
  font-size: 15px;
  color: #000;
  margin: 0;
  font-family: 'Raleway', sans-serif;
}

/* ===== TOGGLE BUTTON ===== */
.review-toggle-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #3B6152;
  font-size: 15px;
  font-weight: 600;
  font-family: 'Raleway', sans-serif;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  flex-shrink: 0;
  padding: 0;
  white-space: nowrap;
}

.review-toggle-chevron {
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.review-toggle-chevron.rotated {
  transform: rotate(180deg);
}

/* ===== FORM CONTAINER ===== */
.review-form-container {
  background: #fff;
  border-radius: 0 0 14px 14px;
  padding: 0 20px 28px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
  margin-top: -1px;
}

.review-form-divider {
  height: 1px;
  background: #e8e6e2;
  margin-bottom: 28px;
}

/* ===== TWO-COLUMN GRID ===== */
.review-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: start;
}

/* ===== RATING COLUMN ===== */
.review-ratings-col {
  display: flex;
  flex-direction: column;
}

.review-rating-group {
  padding-bottom: 20px;
}

.review-rating-label {
  font-size: 15px;
  font-weight: 600;
  color: #3B6152;
  text-transform: uppercase;
  font-family: 'Raleway', sans-serif;
  margin: 0 0 6px 0;
}

.review-rating-desc {
  font-size: 15px;
  color: #000;
  margin: 0 0 10px 0;
  font-family: 'Raleway', sans-serif;
  line-height: 1.5;
}

.review-stars-row {
  display: flex;
  gap: 4px;
  align-items: center;
}

.review-star-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #d4b896;
  cursor: pointer;
  transition: color 0.15s ease, transform 0.1s ease;
  padding: 0;
  line-height: 1;
}

.review-star-btn:hover,
.review-star-btn.active {
  color: #3B6152;
}

.review-star-btn:hover {
  transform: scale(1.1);
}

/* ===== COMMENT COLUMN ===== */
.review-comment-col {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.review-textarea {
  width: 100%;
  padding: 14px;
  border: 1.5px solid #e5e3df;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  color: #333;
  resize: vertical;
  min-height: 180px;
  background: #fafaf9;
  outline: none;
  transition: border-color 0.18s;
  box-sizing: border-box;
}

.review-textarea:focus {
  border-color: #3B6152;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(59, 97, 82, 0.08);
}

.review-char-count {
  font-size: 12px;
  color: #aaa;
  margin: 0;
  text-align: right;
  font-family: 'Raleway', sans-serif;
}

/* ===== FORM ACTIONS ===== */
.review-form-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 28px;
}

.review-submit-btn {
  padding: 14px 48px;
  background: #3B6152;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  font-family: 'Raleway', sans-serif;
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s ease;
}

.review-submit-btn:hover:not(:disabled) {
  background: #2e4f42;
}

.review-submit-btn:disabled {
  background: #c5c5c5;
  cursor: not-allowed;
}

.review-error-msg {
  color: #d32f2f;
  font-size: 14px;
  margin: 0;
  font-family: 'Raleway', sans-serif;
  text-align: center;
}

.review-success-msg {
  color: #3B6152;
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  font-family: 'Raleway', sans-serif;
  text-align: center;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 767px) {
  .reservation-review-section {
    padding: 24px 16px;
  }

  .review-wrapper {
    max-width: 100%;
  }

  .review-title {
    font-size: 20px;
  }

  .review-subtitle {
    font-size: 14px;
    margin-bottom: 16px;
  }

  /* Lodging card stacks on mobile */
  .review-lodging-card {
    flex-wrap: wrap;
    gap: 12px;
    padding: 12px 16px;
    align-items: flex-start;
  }

  .review-lodging-image {
    width: 100%;
    height: 180px;
    border-radius: 10px;
    align-self: auto;
  }

  .review-lodging-body {
    width: 100%;
  }

  .review-toggle-btn {
    width: 100%;
    justify-content: flex-end;
    padding-top: 8px;
    border-top: 1px solid #f0efed;
  }

  /* Form container */
  .review-form-container {
    padding: 0 16px 24px;
    border-radius: 0 0 14px 14px;
  }

  /* Single column on mobile */
  .review-form-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .review-textarea {
    min-height: 140px;
  }

  .review-submit-btn {
    width: 100%;
    padding: 14px 24px;
  }
}

@media (max-width: 480px) {
  .review-title {
    font-size: 18px;
  }

  .review-lodging-name {
    font-size: 14px;
  }
}
</style>
